import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  TuiIslandDirective,
  TuiTextfieldControllerModule,
  TuiTextareaModule,
  TuiComboBoxModule,
  TuiInputModule,
  TuiInputDateModule,
  TuiInputDateRangeModule,
  TuiInputNumberModule,
  TuiInputTimeModule,
  TuiMultiSelectModule,
  TuiSelectModule,
  TuiTagModule,
} from '@taiga-ui/legacy';
import {
  TuiNotification,
  TuiFormatNumberPipe,
  TuiDataList,
  TuiError,
  TuiLoader,
  TuiScrollbar,
  TuiScrollable,
  TuiGroup,
  TuiLabel,
  TuiDropdown,
  TuiIcon,
  TuiDialog,
  TuiButton,
  TuiHint,
  TuiNumberFormat,
  TuiTextfield,
} from '@taiga-ui/core';
import {
  TuiPagination,
  TuiCarousel,
  TuiFieldErrorPipe,
  TuiFieldErrorContentPipe,
  TuiBadge,
  TuiSwitch,
  TuiTabs,
  TuiRadioList,
  TuiButtonLoading,
  TuiSkeleton,
  TuiCheckbox,
} from '@taiga-ui/kit';
import { TuiRepeatTimes, TuiLet } from '@taiga-ui/cdk';
import { TuiAxes, TuiBarChart } from '@taiga-ui/addon-charts';
import { TuiTablePagination, TuiTable } from '@taiga-ui/addon-table';
import { TuiDropdownMobile } from '@taiga-ui/addon-mobile';
import { QRCodeModule } from 'angularx-qrcode';
import { NgxPermissionsModule } from 'ngx-permissions';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { ComponentsModule } from '@src/app/components';
import { SharedModule } from '@src/app/shared';
import { IconModule } from '@src/app/modules/icon';
import { PhoneModule } from '@src/app/modules/phone';
import { SearchUsersModule } from '@src/app/modules/search-users';
import { PhotoModule } from '@src/app/modules/photo';
import { CounterModule } from '@src/app/modules/counter';
import { LogoModule } from '@src/app/modules/logo';
import { CarouselModule } from '@src/app/modules/carousel';
import { FieldModule } from '@src/app/shared/field';
import { SearchCitiesModule } from '@src/app/modules/search-cities';
import { MapModule } from '@src/app/modules/map';
import { ListModeSwitcherModule } from '@src/app/modules/list-mode-switcher';
import { BarcodeModule, DropdownButtonModule } from '@src/app/modules/common';
import { InputFilesModule } from '@src/app/modules/input-files';
import { ImagesCarouselModule } from '@src/app/modules/images-carousel';
import { EditorModule } from '@src/app/modules/editor';
import { ActionsModule } from '@src/app/modules/actions';

import { CustomNamesTabsModule } from '../custom-name-tabs';
import { GeocoderModule } from '../geocoder';

import { LoyaltyProgramComponent } from './loyalty-program.component';
import {
  HeaderComponent,
  AddButtonComponent,
  CategoryCellComponent,
  NoDataFoundComponent,
  ProductCardComponent,
  DiscountTagComponent,
  FavoriteButtonComponent,
  ProductCodeCardComponent,
  ExportCodesButtonComponent,
  CategoriesButtonComponent,
  ProductListCardComponent,
} from './components';
import {
  ViewHeaderComponent,
  ViewImagesCarouselComponent,
  ViewInfoBlockComponent,
  ViewContactsBlockComponent,
  DiscountDialogComponent,
  DiscountGroupsDialogComponent,
  DiscountGroupRowComponent,
  SimilarProductsComponent,
} from './lp-view-product/components';
import { LpOwnProductsHeaderComponent } from './lp-own-products/components';
import { ResultDialogComponent, ConfirmDialogComponent } from './ui';
import {
  ProductDialogComponent,
  ProductStepsWizardComponent,
  CommonStepComponent,
  ContactsStepComponent,
  TermsStepComponent,
  AbstractStepComponent,
} from './product-dialog';
import { LpViewProductComponent } from './lp-view-product/lp-view-product.component';
import { LpOwnProductsComponent } from './lp-own-products/lp-own-products.component';
import { NavButtonComponent } from './components/nav-button/nav-button.component';
import { GetCategoryNamePipe } from './pipes';
import { LpAllStatisticsComponent } from './lp-all-statistics/lp-all-statistics.component';
import { StatisticsButtonComponent } from './components/statistics-button/statistics-button.component';
import { LpStatisticsHeaderComponent } from './lp-all-statistics/components/lp-statistics-header/lp-statistics-header.component';
import { LpStatisticsCardComponent } from './lp-all-statistics/components/lp-statistics-card/lp-statistics-card.component';
import { LpStatisticsBarChartComponent } from './lp-all-statistics/components/lp-statistics-bar-chart/lp-statistics-bar-chart.component';
import { LpStatisticsTableComponent } from './lp-all-statistics/components/lp-statistics-table/lp-statistics-table.component';
import { LpStatisticsWidgetHeaderComponent } from './lp-all-statistics/components/lp-statistics-widget-header/lp-statistics-widget-header.component';
import { LpFavoriteProductsComponent } from './lp-favorite-products/lp-favorite-products.component';
import { LpFavoriteProductsHeaderComponent } from './lp-favorite-products/components';
import { LpMyProductCodesComponent } from './lp-my-product-codes/lp-my-product-codes.component';
import { LpMyProductCodesHeaderComponent } from './lp-my-product-codes/components';
import { CategorySelectionComponent } from './components/category-selection/category-selection.component';
import { CategoryHeaderComponent } from './components/category-header/category-header.component';
import { LpCarouselCategoriesComponent } from './lp-carousel-categories/lp-carousel-categories.component';
import { LpSearchProductsInputComponent } from './lp-search-products-input/lp-search-products-input.component';
import { ProductRowItemComponent } from './lp-search-products-input/components/product-row-item/product-row-item.component';
import {
  LpCollectionInfoComponent,
  LpCollectionListComponent,
  LpCollectionsComponent,
  CollectionInfoHeaderComponent,
  LpCollectionEditComponent,
  LpCollectionViewComponent,
  LpCollectionItemComponent,
  LpCollectionListHeaderComponent,
} from './lp-collections';
import { LpCollectionStatisticsComponent } from './lp-collection-statistics/lp-collection-statistics.component';
import {
  LpProductRequestComponent,
  LpProductRequestListComponent,
  LpProductRequestInfoComponent,
  ProductRequestListHeaderComponent,
  ProductRequestListItemComponent,
  LpProductRequestEditComponent,
  LpProductRequestViewComponent,
  ProductRequestInfoHeaderComponent,
  LpProductRequestButtonComponent,
  SuccessfulSaveDialogComponent,
  GetStatusTextPipe,
  ProductRequestChangesComponent,
} from './lp-product-request';
import { LpCollectionNewComponent, LpCollectionNewHeaderComponent } from './lp-collection-new';
import { ProductsListComponent } from './components/products-list/products-list.component';
import { CategoriesFieldComponent } from './components/categories-field/categories-field.component';
import { ProductsListCategoryComponent } from './components/products-list-category/products-list-category.component';
import { CityDialogComponent } from './dialogs/city-dialog/city-dialog.component';
import { CategoriesDialogComponent } from './dialogs/categories-dialog/categories-dialog.component';
import { ProductsCarouselComponent } from './components/products-carousel/products-carousel.component';

@NgModule({
  declarations: [
    LoyaltyProgramComponent,
    HeaderComponent,
    AddButtonComponent,
    CategoryCellComponent,
    NoDataFoundComponent,
    ProductCardComponent,
    LpViewProductComponent,
    LpOwnProductsComponent,
    ViewHeaderComponent,
    ViewImagesCarouselComponent,
    ViewInfoBlockComponent,
    ViewContactsBlockComponent,
    DiscountDialogComponent,
    ResultDialogComponent,
    ProductDialogComponent,
    ProductStepsWizardComponent,
    CommonStepComponent,
    ContactsStepComponent,
    TermsStepComponent,
    AbstractStepComponent,
    ConfirmDialogComponent,
    NavButtonComponent,
    DiscountTagComponent,
    LpOwnProductsHeaderComponent,
    GetCategoryNamePipe,
    LpAllStatisticsComponent,
    StatisticsButtonComponent,
    LpStatisticsHeaderComponent,
    LpStatisticsCardComponent,
    LpStatisticsBarChartComponent,
    LpStatisticsTableComponent,
    LpStatisticsWidgetHeaderComponent,
    LpFavoriteProductsComponent,
    LpFavoriteProductsHeaderComponent,
    FavoriteButtonComponent,
    DiscountGroupsDialogComponent,
    DiscountGroupRowComponent,
    LpMyProductCodesComponent,
    LpMyProductCodesHeaderComponent,
    ProductCodeCardComponent,
    SimilarProductsComponent,
    CategorySelectionComponent,
    CategoryHeaderComponent,
    ExportCodesButtonComponent,
    LpCarouselCategoriesComponent,
    LpSearchProductsInputComponent,
    ProductRowItemComponent,
    LpCarouselCategoriesComponent,
    LpCollectionListComponent,
    LpCollectionItemComponent,
    LpCollectionListHeaderComponent,
    LpCollectionInfoComponent,
    LpCollectionViewComponent,
    CollectionInfoHeaderComponent,
    LpCollectionEditComponent,
    LpCollectionsComponent,
    LpCollectionStatisticsComponent,
    CategoriesButtonComponent,
    ProductListCardComponent,
    LpProductRequestComponent,
    LpProductRequestListComponent,
    LpProductRequestInfoComponent,
    ProductRequestListHeaderComponent,
    ProductRequestListItemComponent,
    LpProductRequestEditComponent,
    LpProductRequestViewComponent,
    ProductRequestInfoHeaderComponent,
    LpProductRequestButtonComponent,
    SuccessfulSaveDialogComponent,
    GetStatusTextPipe,
    ProductRequestChangesComponent,
    LpCollectionNewComponent,
    LpCollectionNewHeaderComponent,
    ProductsListComponent,
    CategoriesFieldComponent,
    ProductsListComponent,
    ProductsListCategoryComponent,
    CityDialogComponent,
    CategoriesDialogComponent,
    ProductsCarouselComponent,
  ],
  imports: [
    CommonModule,
    TuiButton,
    TuiInputModule,
    FormsModule,
    IconModule,
    TuiTextfieldControllerModule,
    TuiSelectModule,
    ...TuiDataList,
    TuiMultiSelectModule,
    TuiLabel,
    TuiBadge,
    TuiIcon,
    TuiTagModule,
    RouterModule,
    ...TuiDropdown,
    ...TuiCarousel,
    TuiPagination,
    ComponentsModule,
    ...TuiHint,
    TuiTextareaModule,
    TuiFieldErrorPipe,
    TuiFieldErrorContentPipe,
    ReactiveFormsModule,
    TuiError,
    TuiRepeatTimes,
    TuiInputNumberModule,
    TuiComboBoxModule,
    QRCodeModule,
    SharedModule,
    TuiLoader,
    TuiScrollbar,
    TuiScrollable,
    PhoneModule,
    NgxPermissionsModule,
    InfiniteScrollModule,
    TuiRadioList,
    SearchUsersModule,
    CustomNamesTabsModule,
    PhotoModule,
    TuiIslandDirective,
    TuiAxes,
    TuiBarChart,
    TuiInputDateRangeModule,
    ...TuiTable,
    TuiTablePagination,
    TuiLet,
    CounterModule,
    TuiFormatNumberPipe,
    TuiNotification,
    TuiDialog,
    ...TuiTabs,
    TuiInputDateModule,
    TuiGroup,
    LogoModule,
    CarouselModule,
    FieldModule,
    TuiDropdownMobile,
    TuiInputTimeModule,
    SearchCitiesModule,
    MapModule,
    GeocoderModule,
    ListModeSwitcherModule,
    BarcodeModule,
    DropdownButtonModule,
    TuiSwitch,
    InputFilesModule,
    ImagesCarouselModule,
    EditorModule,
    TuiButtonLoading,
    TuiNumberFormat,
    TuiSkeleton,
    TuiCheckbox,
    ...TuiTextfield,
    ActionsModule,
  ],
  exports: [
    LoyaltyProgramComponent,
    LpViewProductComponent,
    LpOwnProductsComponent,
    LpAllStatisticsComponent,
    LpFavoriteProductsComponent,
    LpMyProductCodesComponent,
    LpCollectionListComponent,
    LpCollectionInfoComponent,
    LpProductRequestComponent,
    CategoriesFieldComponent,
  ],
})
export class LoyaltyProgramModule {}
